import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyDateSelectionComponent } from '../../formly/formly-date-selection/formly-date-selection.component';
import { FormlyFileSelectionComponent } from '../../formly/formly-file-selection/formly-file-selection.component';
import { SiteService } from '../../../services/data/site.service';
import { EnumTranslationService } from '../../../services/enum-translation.service';
import { CountryService } from '../../../services/data/country.service';
import { BusinessVisaTypeEnum } from '../../../models/business-visa-type.enum';
import { formatDateTime } from '../../formly/formly-date-selection/date-validator';
import { VisitorRegistrationFormStatus } from '../../../models/visitor-registration-form-status';

export function getRegistrationFormHeader(
  siteService: SiteService,
  enumTranslationService: EnumTranslationService,
): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<h2>Registration Form</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-6',
          id: 'status',
          key: 'form.status',
          type: 'select',
          props: {
            label: 'Status',
            labelPosition: 'floating',
            placeholder: 'Status',
            disabled: true,
            options: enumTranslationService.getRegistrationFormStatusOptions(),
            valueProp: 'id',
            labelProp: 'value',
          },
        },
        {
          className: 'col-md-6',
          id: 'site',
          key: 'form.siteId',
          type: 'select',
          props: {
            label: 'Site',
            labelPosition: 'floating',
            placeholder: 'Select Site',
            required: true,
            options: siteService.sites$,
            valueProp: 'id',
            labelProp: 'name',
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      validators: {
        validation: [
          {
            name: 'departureValidator',
            options: { errorPath: 'form.expectedDepartureDate' },
          },
        ],
      },
      fieldGroup: [
        {
          className: 'col-md-6',
          id: 'expectedArrivalDate',
          key: 'form.expectedArrivalDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Expected Arrival',
            labelPosition: 'floating',
            placeholder: 'expectedArrivalDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-6',
          id: 'expectedDepartureDate',
          key: 'form.expectedDepartureDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Expected Departure',
            labelPosition: 'floating',
            placeholder: 'expectedDepartureDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-8',
          id: 'formApprovedBy',
          key: 'form.approval.approvedBy.name',
          type: 'input',
          props: {
            label: 'HR Approved By',
            labelPosition: 'floating',
            placeholder: 'HR Approved By',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.approval;
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-4',
          id: 'formApprovedOn',
          key: 'form.approval.approvedOn',
          type: 'input',
          props: {
            label: 'HR Approved On',
            labelPosition: 'floating',
            placeholder: 'HR Approved On',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.approval;
            },
            'props.disabled': 'formState.disabled',
          },
          parsers: [(value) => formatDateTime(value)],
        },
        {
          className: 'col-8',
          id: 'formApprovalReviewReason',
          key: 'form.approval.reviewReason',
          type: 'textarea',
          props: {
            label: 'HR Reason For Review',
            labelPosition: 'floating',
            placeholder: 'HR Reason For Review',
            readonly: true,
            rows: 2,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.approval?.reviewReason;
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-4',
          id: 'formApprovalReviewOn',
          key: 'form.approval.reviewOn',
          type: 'input',
          props: {
            label: 'HR Review Deadline',
            labelPosition: 'floating',
            placeholder: 'HR Review Deadline',
            readonly: true,
            rows: 2,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.approval?.reviewOn;
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-8',
          id: 'formEHSApprovedBy',
          key: 'form.ehsApproval.approvedBy.name',
          type: 'input',
          props: {
            label: 'EHS Approved By',
            labelPosition: 'floating',
            placeholder: 'EHS Approved By',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.ehsApproval;
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-4',
          id: 'formEHSApprovedOn',
          key: 'form.ehsApproval.approvedOn',
          type: 'input',
          props: {
            label: 'EHS Approved On',
            labelPosition: 'floating',
            placeholder: 'EHS Approved On',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.ehsApproval;
            },
            'props.disabled': 'formState.disabled',
          },
          parsers: [(value) => formatDateTime(value)],
        },
        {
          className: 'col-8',
          id: 'formEHSApprovalReviewReason',
          key: 'form.ehsApproval.reviewReason',
          type: 'textarea',
          props: {
            label: 'EHS Reason for Review',
            labelPosition: 'floating',
            placeholder: 'EHS Reason for Review',
            readonly: true,
            rows: 2,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.ehsApproval?.reviewReason;
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-4',
          id: 'formEHSApprovalReviewOn',
          key: 'form.ehsApproval.reviewOn',
          type: 'input',
          props: {
            label: 'EHS Review Deadline',
            labelPosition: 'floating',
            placeholder: 'EHS Review Deadline',
            readonly: true,
            rows: 2,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.ehsApproval?.reviewOn;
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-8',
          id: 'formRejectedBy',
          key: 'form.rejection.rejectedBy.name',
          type: 'input',
          props: {
            label: 'Rejected By',
            labelPosition: 'floating',
            placeholder: 'Rejected By',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.rejection;
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-4',
          id: 'formRejectedOn',
          key: 'form.rejection.rejectedOn',
          type: 'input',
          props: {
            label: 'Rejected On',
            labelPosition: 'floating',
            placeholder: 'Rejected On',
            readonly: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.rejection;
            },
            'props.disabled': 'formState.disabled',
          },
          parsers: [(value) => formatDateTime(value)],
        },
        {
          className: 'col-12',
          id: 'formRejectedReason',
          key: 'form.rejection.reason',
          type: 'textarea',
          props: {
            label: 'Rejected Reason',
            labelPosition: 'floating',
            placeholder: 'Rejected Reason',
            readonly: true,
            rows: 2,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return !field.model?.form?.rejection;
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export function getPersonalInformationSection(
  enumTranslationService: EnumTranslationService,
): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>Personal Information</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-2',
          id: 'registrationNumber',
          key: 'personalInformation.registrationNumber',
          type: 'input',
          props: {
            label: 'Reg. No.',
            labelPosition: 'floating',
            placeholder: 'RegistrationNumber',
            disabled: true,
          },
        },
        {
          className: 'col-md-5',
          id: 'firstName',
          key: 'personalInformation.firstName',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'First Name (english)',
            labelPosition: 'floating',
            placeholder: 'firstName',
            required: true,
          },
          validators: {
            validation: ['englishCharactersOnly'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-5',
          id: 'lastName',
          key: 'personalInformation.lastName',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Last Name (english)',
            labelPosition: 'floating',
            placeholder: 'lastName',
            required: true,
          },
          validators: {
            validation: ['englishCharactersOnly'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-8',
          id: 'nativeName',
          key: 'personalInformation.nativeName',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Name in native language',
            labelPosition: 'floating',
            placeholder: 'nativeName',
            required: false,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-4',
          id: 'gender',
          key: 'personalInformation.gender',
          type: 'select',
          props: {
            label: 'Gender',
            labelPosition: 'floating',
            required: true,
            options: enumTranslationService.getGenderOptions(),
            valueProp: 'id',
            labelProp: 'value',
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-8',
          id: 'emailAddress',
          key: 'personalInformation.emailAddress',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Email Address (work)',
            labelPosition: 'floating',
            placeholder: 'emailAddress',
            required: true,
          },
          validators: {
            validation: ['email'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-4',
          id: 'phoneNumber',
          key: 'personalInformation.phoneNumber',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Phone number (work)',
            labelPosition: 'floating',
            placeholder: 'phoneNumber',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export function getPassportSection(
  countryService: CountryService,
): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>Passport</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          key: 'passport.fileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Passport',
            labelPosition: 'floating',
            placeholder: 'passport',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-md-6',
          key: 'passport.passportNumber',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Passport Number',
            labelPosition: 'floating',
            placeholder: 'passportNumber',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-6',
          key: 'passport.issuingCountryId',
          type: 'select',
          props: {
            label: 'Issuing Country',
            labelPosition: 'floating',
            required: true,
            options: countryService.countries$,
            valueProp: 'code',
            labelProp: 'name',
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      validators: {
        validation: [
          {
            name: 'passportValidityValidator',
            options: { errorPath: 'passport.expiryDate' },
          },
        ],
      },
      fieldGroup: [
        {
          className: 'col-md-6',
          id: 'passportIssuedDate',
          key: 'passport.issuedDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Issued date',
            labelPosition: 'floating',
            placeholder: 'passportIssuedDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-6',
          id: 'passportExpiryDate',
          key: 'passport.expiryDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Expiry date',
            labelPosition: 'floating',
            placeholder: 'passportExpiryDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export function getCompanyInformationSection(
  visitingGroupCompulsory: boolean = true,
): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>Company Information</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'companyName',
          key: 'companyInformation.companyName',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Your Company Name',
            labelPosition: 'floating',
            placeholder: 'companyName',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-lg-6',
          id: 'visitingGroupLeadingPersonName',
          key: 'companyInformation.visitingGroupResponsible.name',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Visiting group leading person',
            labelPosition: 'floating',
            placeholder: 'visitingGroupLeadingPersonName',
            required: visitingGroupCompulsory,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-lg-6',
          id: 'visitingGroupLeadingPersonEmailAddress',
          key: 'companyInformation.visitingGroupResponsible.emailAddress',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Visiting group leading person (email)',
            labelPosition: 'floating',
            placeholder: 'visitingGroupLeadingPersonEmailAddress',
            required: visitingGroupCompulsory,
          },
          validators: {
            validation: ['email'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-lg-6',
          id: 'hostCountryResponsibleName',
          key: 'companyInformation.hostCountryResponsible.name',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'AESC Host Country Responsible Person',
            labelPosition: 'floating',
            placeholder: 'hostCountryResponsibleName',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-lg-6',
          id: 'hostCountryResponsibleEmailAddress',
          key: 'companyInformation.hostCountryResponsible.emailAddress',
          type: 'input',
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'AESC Host Country Responsible Person Email',
            labelPosition: 'floating',
            placeholder: 'hostCountryResponsibleEmailAddress',
            required: true,
          },
          validators: {
            validation: ['email'],
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export function getBusinessVisaInformationSection(
  enumTranslationService: EnumTranslationService,
): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>Business Visa</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'businessVisaType',
          key: 'businessVisa.type',
          type: 'select',
          props: {
            label: 'Business Visa Type',
            labelPosition: 'floating',
            required: true,
            options: enumTranslationService.getBusinessVisaOptions(),
            valueProp: 'id',
            labelProp: 'value',
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'businessVisaFile',
          key: 'businessVisa.fileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Visa',
            labelPosition: 'floating',
            placeholder: 'Business Visa',
            required: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return (
                !field.model.businessVisa ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.EuropeanPassport ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.OtherPassport
              );
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      validators: {
        validation: [
          {
            name: 'businessVisaValidityValidator',
            options: { errorPath: 'businessVisa.expiryDate' },
          },
        ],
      },
      fieldGroup: [
        {
          className: 'col-md-6',
          id: 'businessVisaIssuedDate',
          key: 'businessVisa.issuedDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Issued date',
            labelPosition: 'floating',
            placeholder: 'businessVisaIssuedDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return (
                !field.model.businessVisa ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.EuropeanPassport ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.OtherPassport
              );
            },
            'props.disabled': 'formState.disabled',
          },
        },
        {
          className: 'col-md-6',
          id: 'businessVisaExpiryDate',
          key: 'businessVisa.expiryDate',
          type: FormlyDateSelectionComponent,
          modelOptions: {
            updateOn: 'blur',
          },
          props: {
            label: 'Expiry date',
            labelPosition: 'floating',
            placeholder: 'businessVisaExpiryDate',
            required: true,
          },
          validators: {
            validation: ['date'],
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return (
                !field.model.businessVisa ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.EuropeanPassport ||
                field.model.businessVisa.type ===
                  BusinessVisaTypeEnum.OtherPassport
              );
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export const workVisaSection: FormlyFieldConfig[] = [
  {
    className: 'section-label',
    template: '<hr/><h2>Work Visa</h2>',
  },
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-12',
        id: 'workVisaFile',
        key: 'workVisa.fileDescriptor',
        type: FormlyFileSelectionComponent,
        props: {
          label: 'Visa',
          labelPosition: 'floating',
          placeholder: 'Work Visa',
          required: true,
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
  {
    fieldGroupClassName: 'row',
    validators: {
      validation: [
        {
          name: 'workVisaValidityValidator',
          options: { errorPath: 'workVisa.expiryDate' },
        },
      ],
    },
    fieldGroup: [
      {
        className: 'col-md-6',
        id: 'workVisaIssuedDate',
        key: 'workVisa.issuedDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Issued date',
          labelPosition: 'floating',
          placeholder: 'workVisaIssuedDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
      {
        className: 'col-md-6',
        id: 'workVisaExpiryDate',
        key: 'workVisa.expiryDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Expiry date',
          labelPosition: 'floating',
          placeholder: 'workVisaExpiryDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
];

export const workPermitSection: FormlyFieldConfig[] = [
  {
    className: 'section-label',
    template: '<hr/><h2>Work Permit</h2>',
  },
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-12',
        id: 'workPermitFile',
        key: 'workPermit.fileDescriptor',
        type: FormlyFileSelectionComponent,
        props: {
          label: 'Visa',
          labelPosition: 'floating',
          placeholder: 'Work Permit',
          required: true,
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
  {
    fieldGroupClassName: 'row',
    validators: {
      validation: [
        {
          name: 'workPermitValidityValidator',
          options: { errorPath: 'workPermit.expiryDate' },
        },
      ],
    },
    fieldGroup: [
      {
        className: 'col-md-6',
        id: 'workPermitIssuedDate',
        key: 'workPermit.issuedDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Issued date',
          labelPosition: 'floating',
          placeholder: 'workPermitIssuedDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
      {
        className: 'col-md-6',
        id: 'workPermitExpiryDate',
        key: 'workPermit.expiryDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Expiry date',
          labelPosition: 'floating',
          placeholder: 'workPermitExpiryDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
];

export const reasonForVisitSection: FormlyFieldConfig[] = [
  {
    className: 'section-label',
    template: '<hr/><h2>Reason For Visit</h2>',
  },
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-12',
        id: 'visitworkFunction',
        key: 'reasonForVisit.workFunction',
        type: 'input',
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Work Function / Role',
          labelPosition: 'floating',
          placeholder: 'workFunction',
          required: true,
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-12',
        id: 'workDescription',
        key: 'reasonForVisit.workDescription',
        type: 'textarea',
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Work Description',
          labelPosition: 'floating',
          placeholder: 'workDescription',
          required: true,
          rows: 5,
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
];

export const sipsiCertificateSection: FormlyFieldConfig[] = [
  {
    className: 'section-label',
    template: '<hr/><h2>SIPSI Certificate</h2>',
  },
  {
    fieldGroupClassName: 'row',
    fieldGroup: [
      {
        className: 'col-12',
        id: 'sipsiFileDescriptor',
        key: 'sipsi.fileDescriptor',
        type: FormlyFileSelectionComponent,
        props: {
          label: 'SIPSI Certificate',
          labelPosition: 'floating',
          placeholder: 'SIPSI Cert.',
          required: true,
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
  {
    fieldGroupClassName: 'row',
    validators: {
      validation: [
        {
          name: 'sipsiValidityValidator',
          options: { errorPath: 'sipsi.expiryDate' },
        },
      ],
    },
    fieldGroup: [
      {
        className: 'col-md-6',
        id: 'sipsiIssuedDate',
        key: 'sipsi.issuedDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Issued date',
          labelPosition: 'floating',
          placeholder: 'sipsiIssuedDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
      {
        className: 'col-md-6',
        id: 'sipsiExpiryDate',
        key: 'sipsi.expiryDate',
        type: FormlyDateSelectionComponent,
        modelOptions: {
          updateOn: 'blur',
        },
        props: {
          label: 'Expiry date',
          labelPosition: 'floating',
          placeholder: 'sipsiExpiryDate',
          required: true,
        },
        validators: {
          validation: ['date'],
        },
        expressions: {
          'props.disabled': 'formState.disabled',
        },
      },
    ],
  },
];

export function getRequiredDocumentationSection(): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>Legally Required Information</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'technicalSkillsFile',
          key: 'documentation.technicalSkillsFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Proof of technical skills',
            labelPosition: 'floating',
            placeholder: 'Technical skills',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'medicalCertificateFile',
          key: 'documentation.medicalCertificateFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Medical certificate',
            labelPosition: 'floating',
            placeholder: 'Medical certificate',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'assignmentContractFile',
          key: 'documentation.assignmentContractFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Assignment contract',
            labelPosition: 'floating',
            placeholder: 'Assignment contract',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'btpCardFile',
          key: 'documentation.btpCardFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'BTP Card',
            labelPosition: 'floating',
            placeholder: 'BTP Card',
            required: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return (
                field.model?.form?.documentation?.btpCardFileDescriptor == null
              );
            },
            'props.required': (field: FormlyFieldConfig) => {
              return false;
            },
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}

export function getEHSSection(): FormlyFieldConfig[] {
  return [
    {
      className: 'section-label',
      template: '<hr/><h2>EHS</h2>',
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'ehsCertificateFile',
          key: 'documentation.ehsCertificateFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'EHS Certificate',
            labelPosition: 'floating',
            placeholder: 'EHS Certificate',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'safetyInductionFile',
          key: 'documentation.safetyInductionFileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'Note of Safety Induction',
            labelPosition: 'floating',
            placeholder: 'Note of Safety Induction',
            required: true,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          id: 'anfasN1File',
          key: 'documentation.anfasN1FileDescriptor',
          type: FormlyFileSelectionComponent,
          props: {
            label: 'ANFAS N1',
            labelPosition: 'floating',
            placeholder: 'ANFAS N1',
            required: false,
          },
          expressions: {
            'props.disabled': 'formState.disabled',
          },
        },
      ],
    },
  ];
}
